import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { trackSubscriptionCheckoutStarted } from '../utils/productAnalytics';
import {
    SparklesIcon,
    CheckCircleIcon,
    RocketLaunchIcon,
    ShieldCheckIcon,
    StarIcon,
    CurrencyDollarIcon,
    ChatBubbleBottomCenterTextIcon,
    DocumentTextIcon,
} from '@heroicons/react/24/outline';

const Pricing = () => {
    const creditPackages = [
        {
            amount: 5,
            credits: 5,
            label: 'Starter',
            promise: 'Rapport + CV restructuré',
            detail: 'Détail du score, toutes les recos, mots-clés ATS et CV à copier. Relances après corrections.',
        },
        {
            amount: 10,
            credits: 12,
            popular: true,
            bonus: '+2 crédits offerts',
            label: 'Essentiel',
            promise: 'Candidature complète',
            detail: 'Le rapport, le CV, et de quoi générer une lettre de motivation.',
        },
        {
            amount: 20,
            credits: 26,
            bonus: '+6 crédits offerts',
            label: 'Pro',
            promise: 'Multi-candidatures',
            detail: 'Relances, matching d’offres et assistant IA.',
        },
        {
            amount: 50,
            credits: 70,
            bonus: '+20 crédits offerts',
            label: 'Expert',
            promise: 'Usage intensif',
            detail: 'Pour enchaîner les versions de CV et les offres.',
        },
    ];

    const features = [
        { icon: SparklesIcon, title: 'Rapport complet', desc: 'Détail du score, points forts, toutes les recommandations et mots-clés ATS.', cost: 'inclus dès 5 €' },
        {
            icon: DocumentTextIcon,
            title: 'CV restructuré',
            desc: 'Version claire à copier ou télécharger (TXT / Word), après l’analyse IA.',
            cost: 'inclus dès 5 €',
        },
        {
            icon: ChatBubbleBottomCenterTextIcon,
            title: 'Lettre de motivation',
            desc: 'Lettre générée à partir de votre CV, ou ciblée sur une offre.',
            cost: 'dès 10 €',
        },
        {
            icon: StarIcon,
            title: 'CV Word premium',
            desc: 'Mise en page professionnelle et fichier Word modifiable.',
            cost: 'option',
        },
        { icon: RocketLaunchIcon, title: 'Matching offre d\'emploi', desc: 'Comparez votre CV à une offre et obtenez un score de compatibilité.', cost: 'relances' },
    ];

    return (
        <div className="min-h-screen bg-slate-200 pt-20 pb-16 px-4">
            <Helmet>
                <title>Tarifs analyse CV IA - CV Analyse | À partir de 5€</title>
                <meta name="description" content="Rapport CV complet et CV restructuré dès 5 €. Aperçu offert : score et 3 conseils. Sans abonnement." />
                <meta name="keywords" content="tarif analyse CV IA, prix analyse CV, coût analyse CV, CV Analyse prix, rapport CV, crédits CV" />
                <link rel="canonical" href="https://www.cv-analyse.fr/pricing" />
                <script type="application/ld+json">{`
                {
                    "@context": "https://schema.org",
                    "@type": "WebPage",
                    "name": "Tarifs CV Analyse",
                    "description": "Rapport CV complet et CV restructuré dès 5 €",
                    "url": "https://www.cv-analyse.fr/pricing",
                    "offers": [
                        {"@type": "Offer", "name": "Pack Starter", "price": "5", "priceCurrency": "EUR"},
                        {"@type": "Offer", "name": "Pack Essentiel", "price": "10", "priceCurrency": "EUR"},
                        {"@type": "Offer", "name": "Pack Pro", "price": "20", "priceCurrency": "EUR"},
                        {"@type": "Offer", "name": "Pack Expert", "price": "50", "priceCurrency": "EUR"}
                    ]
                }
                `}</script>
            </Helmet>

            <div className="max-w-5xl mx-auto">

                {/* Hero */}
                <div className="mb-10">
                    <h1 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-3">
                        Un rapport utilisable, pas un stock d&apos;analyses
                    </h1>
                    <p className="text-slate-600 max-w-2xl mb-4">
                        Dès 5 € : le rapport complet et le CV restructuré. Sans abonnement, sans engagement.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <span className="bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1">
                            <CheckCircleIcon className="w-4 h-4 text-blue-600" /> Aperçu offert : score + 3 conseils
                        </span>
                        <span className="bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1">
                            <ShieldCheckIcon className="w-4 h-4 text-blue-600" /> Paiement sécurisé Stripe
                        </span>
                    </div>
                </div>

                <div className="card-surface p-6 sm:p-8 mb-10">
                    <h2 className="text-xl font-semibold text-slate-900 mb-5">Inclus dès le paiement, puis crédits pour la suite</h2>
                    <div className="grid md:grid-cols-2 gap-3">
                        {features.map(({ icon: Icon, title, desc, cost }) => (
                            <div key={title} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100">
                                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Icon className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <h3 className="font-semibold text-gray-800">{title}</h3>
                                        <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{cost}</span>
                                    </div>
                                    <p className="text-sm text-gray-500">{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Packs */}
                <h2 className="text-xl font-semibold text-slate-900 mb-4">Choisissez votre pack</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                    {creditPackages.map((pkg) => (
                        <div
                            key={pkg.amount}
                            className={`card-surface p-6 text-center relative ${pkg.popular ? 'border-blue-600' : ''}`}
                        >
                            {pkg.popular && (
                                <p className="text-xs font-medium text-blue-700 mb-2">Le plus choisi</p>
                            )}
                            <p className="text-sm font-semibold text-gray-500 mb-2">{pkg.label}</p>
                            <div className="flex items-center justify-center gap-1 mb-1">
                                <CurrencyDollarIcon className="w-5 h-5 text-blue-500" />
                                <span className="text-4xl font-bold text-gray-900">{pkg.amount}€</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-800 mb-2">{pkg.promise}</p>
                            <p className="text-xs text-gray-500 mb-3 min-h-[3rem]">{pkg.detail}</p>
                            <p className="text-xs text-gray-400">{pkg.credits} crédits inclus</p>
                            {pkg.bonus ? (
                                <p className="text-xs font-semibold text-green-600 mb-4">{pkg.bonus}</p>
                            ) : (
                                <div className="mb-4" />
                            )}
                            <Link
                                to="/subscription"
                                onClick={() => trackSubscriptionCheckoutStarted()}
                                className={`block w-full py-2.5 rounded-lg font-medium text-sm text-center ${
                                    pkg.popular
                                        ? 'btn-primary'
                                        : 'btn-secondary'
                                }`}
                            >
                                Commencer
                            </Link>
                        </div>
                    ))}
                </div>

                {/* FAQ */}
                <div className="card-surface p-6 sm:p-8 mb-10">
                    <h2 className="text-xl font-semibold text-slate-900 mb-6">Questions fréquentes</h2>
                    <div className="space-y-5">
                        {[
                            { q: 'Les crédits expirent-ils ?', r: 'Non. Vos crédits sont valables sans limite de durée.' },
                            { q: 'Puis-je essayer gratuitement ?', r: 'Oui. Score et 3 conseils sans inscription ni carte. Après paiement (dès 5 €), réimportez le CV pour le rapport IA complet (détail, ATS, CV restructuré).' },
                            { q: 'Comment fonctionne le parrainage ?', r: 'Parrainez un ami : il reçoit +1 crédit bonus, et vous gagnez +1 crédit quand il fait sa première analyse.' },
                            { q: 'Puis-je obtenir une facture ?', r: 'Oui. Stripe génère une facture automatique après chaque achat, envoyée à votre email.' },
                            { q: 'Le paiement est-il sécurisé ?', r: 'Oui. Les paiements sont traités par Stripe, certifié PCI-DSS niveau 1. Nous ne stockons aucune donnée bancaire.' },
                        ].map(({ q, r }) => (
                            <div key={q} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                                <p className="font-semibold text-gray-800 mb-1">{q}</p>
                                <p className="text-gray-500 text-sm">{r}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CTA final */}
                <div className="cta-band text-center">
                    <Link to="/" className="btn-primary">
                        Voir mon score
                    </Link>
                    <p className="text-sm text-slate-300 mt-3">Score et 3 conseils sans inscription — aucune carte bancaire requise</p>
                </div>
            </div>
        </div>
    );
};

export default Pricing;
